/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  difference,
  differenceWith,
  groupBy,
  isEqual,
  mapValues,
  omit,
  omitBy,
  uniqWith,
} from 'lodash'

import type { ProjectRoleAssignments } from '@modules/cloud-api/v1/types'
import type { Dictionary } from '@modules/ts-essentials'
import type { ProjectType } from '@modules/ui-types/projects'

import { filterRoleAssignments, normaliseProjectRoleAssignments } from '.'

import type {
  NormalisedResourceRoleAssignment,
  ResourceRoleAssignment,
  ResourceRoleAssignmentsDiff,
} from './types'

const diffProjectRoleAssignments = (
  original: ProjectRoleAssignments | undefined,
  changed: ProjectRoleAssignments | undefined,
  isApiKey?: boolean,
): ResourceRoleAssignmentsDiff => {
  const { added: addedElasticsearch, removed: removedElasticsearch } = diff(
    normaliseProjectRoleAssignments('elasticsearch')(original?.elasticsearch),
    normaliseProjectRoleAssignments('elasticsearch')(changed?.elasticsearch),
  )

  const { added: addedObservability, removed: removedObservability } = diff(
    normaliseProjectRoleAssignments('observability')(original?.observability),
    normaliseProjectRoleAssignments('observability')(changed?.observability),
  )

  const { added: addedSecurity, removed: removedSecurity } = diff(
    normaliseProjectRoleAssignments('security')(original?.security),
    normaliseProjectRoleAssignments('security')(changed?.security),
  )

  return {
    added: {
      elasticsearch: toProjectRoleAssignments(addedElasticsearch),
      observability: toProjectRoleAssignments(addedObservability),
      security: toProjectRoleAssignments(addedSecurity),
    },
    removed: {
      elasticsearch: toProjectRoleAssignments(removedElasticsearch),
      observability: toProjectRoleAssignments(removedObservability),
      security: toProjectRoleAssignments(removedSecurity),
    },
  }

  function diff<T extends ProjectType>(
    originalRoleAssignments: Array<NormalisedResourceRoleAssignment<T>> = [],
    changedRoleAssignments: Array<NormalisedResourceRoleAssignment<T>> = [],
  ): {
    added: Array<NormalisedResourceRoleAssignment<T>>
    removed: Array<NormalisedResourceRoleAssignment<T>>
  } {
    const allRoleAssignments = diffAllRoleAssignments()
    const specificRoleAssignments = diffPredefinedAssignments()
    const applicationRoles = diffCustomAssignments()

    return {
      added: uniqWith(
        [...allRoleAssignments.added, ...specificRoleAssignments.added, ...applicationRoles.added],
        isEqualApplicationRoles,
      ),

      removed: uniqWith(
        [
          ...allRoleAssignments.removed,
          ...specificRoleAssignments.removed,
          ...applicationRoles.removed,
        ],
        isEqualApplicationRoles,
      ),
    }

    function diffCustomAssignments(): {
      added: Array<NormalisedResourceRoleAssignment<T>>
      removed: Array<NormalisedResourceRoleAssignment<T>>
    } {
      const originalApplicationRoles = filterRoleAssignments(
        originalRoleAssignments,
        'applicationRoleAssignments',
        isApiKey,
      )

      const changedApplicationRoles = filterRoleAssignments(
        changedRoleAssignments,
        'applicationRoleAssignments',
        isApiKey,
      )

      const originalCustomApplicationRoles = filterRoleAssignments(
        originalApplicationRoles,
        'customApplicationRoleAssignments',
        isApiKey,
      )

      const changedCustomApplicationRoles = filterRoleAssignments(
        changedApplicationRoles,
        'customApplicationRoleAssignments',
        isApiKey,
      )

      // The expectation here is that assignment roles containing custom application roles are
      // separate from assignment roles with predefined roles (even those including application roles)
      // this means that if the change only involves custom application roles,
      // we can return the normal differenceWith result
      if (
        originalCustomApplicationRoles.length === originalApplicationRoles.length &&
        changedCustomApplicationRoles.length === changedApplicationRoles.length
      ) {
        const removed = differenceWith(
          originalApplicationRoles,
          changedApplicationRoles,
          isEqualApplicationRoles,
        )

        const added = differenceWith(
          changedApplicationRoles,
          originalApplicationRoles,
          isEqualApplicationRoles,
        )

        return { added, removed }
      }

      // If predefined roles are involved, which indicates API stack access change is involved,
      // we need to include the affected roleIds from the original and changed role assignments,
      // even if they do not have application roles. This is to handle the scenario where user
      // toggles API access for a role, which can lead to application roles going from undefined
      // to an array or empty array & vice versa, which would be missed by filter results above

      const originalApplicationRoleIdsSet = getRoleAssignmentIdLabelSets(originalApplicationRoles)
      const changedApplicationRoleIdsSet = getRoleAssignmentIdLabelSets(changedApplicationRoles)

      // Find the role assignments with exact project ids that are affected by the change in the diff
      // The project ids match is needed because there can be multiple role assignments with the same role
      // id (i.e. viewer) for different projects
      const originalApplicationRolesAffected = originalRoleAssignments.filter((roleAssignment) => {
        const labels = getRoleAssignmentIdLabels(roleAssignment)
        return labels.some((label) => changedApplicationRoleIdsSet.has(label))
      })
      const changedApplicationRolesAffected = changedRoleAssignments.filter((roleAssignment) => {
        const labels = getRoleAssignmentIdLabels(roleAssignment)
        return labels.some((label) => originalApplicationRoleIdsSet.has(label))
      })

      // Merge and get the unique affected role assignments with the original role assignments,
      // and the unique affected role assignments with the changed role assignments.
      const mergedOriginalApplicationRoles = uniqWith(
        [...originalApplicationRolesAffected, ...originalApplicationRoles],
        isEqualApplicationRoles,
      )

      const mergedChangedApplicationRoles = uniqWith(
        [...changedApplicationRolesAffected, ...changedApplicationRoles],
        isEqualApplicationRoles,
      )

      const removed = differenceWith(
        mergedOriginalApplicationRoles,
        mergedChangedApplicationRoles,
        isEqualApplicationRoles,
      )

      const added = differenceWith(
        mergedChangedApplicationRoles,
        mergedOriginalApplicationRoles,
        isEqualApplicationRoles,
      )

      return { added, removed }
    }

    function diffAllRoleAssignments(): {
      added: Array<NormalisedResourceRoleAssignment<T>>
      removed: Array<NormalisedResourceRoleAssignment<T>>
    } {
      const originalAllRoleAssignments = filterRoleAssignments(
        originalRoleAssignments,
        'allRoleAssignments',
      )

      const changedAllRoleAssignments = filterRoleAssignments(
        changedRoleAssignments,
        'allRoleAssignments',
      )

      return {
        added: differenceWith(changedAllRoleAssignments, originalAllRoleAssignments, isEqual),
        removed: differenceWith(originalAllRoleAssignments, changedAllRoleAssignments, isEqual),
      }
    }

    function diffPredefinedAssignments(): {
      added: Array<NormalisedResourceRoleAssignment<T>>
      removed: Array<NormalisedResourceRoleAssignment<T>>
    } {
      const originalSpecificRoleAssignments = filterRoleAssignments(
        originalRoleAssignments,
        'specificPredefinedRoleAssignments',
      )

      const originalIds = getIdsByRoleId(originalSpecificRoleAssignments)

      const changedSpecificRoleAssignments = filterRoleAssignments(
        changedRoleAssignments,
        'specificPredefinedRoleAssignments',
      )

      const changedIds = getIdsByRoleId(changedSpecificRoleAssignments)

      const added = diffIds(changedSpecificRoleAssignments, originalIds).filter(
        ({ ids = [] }) => ids.length > 0,
      )

      const removed = diffIds(originalSpecificRoleAssignments, changedIds).filter(
        ({ ids = [] }) => ids.length > 0,
      )

      return { added, removed }

      function getIdsByRoleId(
        roleAssignments: Array<NormalisedResourceRoleAssignment<T>>,
      ): Record<string, string[]> {
        const roleAssignmentsGroupedByRoleId = groupBy(roleAssignments, 'role_id')

        return mapValues(roleAssignmentsGroupedByRoleId, (groupedRoleAssignments) =>
          groupedRoleAssignments.flatMap(({ ids }) => ids ?? []),
        )
      }

      function diffIds(
        roleAssignments: Array<NormalisedResourceRoleAssignment<T>>,
        idsByRoleId: Dictionary<string[]>,
      ): Array<NormalisedResourceRoleAssignment<T>> {
        return roleAssignments.map(
          (roleAssignment): NormalisedResourceRoleAssignment<T> => ({
            ...roleAssignment,
            ids: difference(roleAssignment.ids, idsByRoleId[roleAssignment.role_id] ?? []),
          }),
        )
      }
    }
  }
}

const toProjectRoleAssignments = (
  roleAssignments: Array<NormalisedResourceRoleAssignment<ProjectType>>,
): Array<ResourceRoleAssignment<ProjectType>> =>
  roleAssignments.map((roleAssignment) => {
    if (roleAssignment.all) {
      return roleAssignment
    }

    const convertedRoleAssignment = {
      ...roleAssignment,
      project_ids: roleAssignment.ids,
    }

    return omit(convertedRoleAssignment, 'ids')
  })

function isEqualApplicationRoles<T extends ProjectType>(
  a: NormalisedResourceRoleAssignment<T>,
  b: NormalisedResourceRoleAssignment<T>,
): boolean {
  // Lodash's deep equal distinguishes between undeclared and undefined,
  // so we need to create normalized versions where undefined application_roles is omitted
  // and is evaluated as equal to undeclared application_roles
  const normalizeApplicationRoles = (roleAssignment: NormalisedResourceRoleAssignment<T>) =>
    omitBy(roleAssignment, (value) => value === undefined)

  return isEqual(normalizeApplicationRoles(a), normalizeApplicationRoles(b))
}

export function getRoleAssignmentIdLabels<T extends ProjectType>(
  roleAssignment: NormalisedResourceRoleAssignment<T>,
): string[] {
  if (roleAssignment.ids) {
    if (roleAssignment.ids.length > 0) {
      return roleAssignment.ids.map((id) => `${roleAssignment.role_id}:${id}`)
    }

    return [`${roleAssignment.role_id}:empty`]
  }

  return [`${roleAssignment.role_id}:undefined`]
}

export function getRoleAssignmentIdLabelSets<T extends ProjectType>(
  roleAssignments: Array<NormalisedResourceRoleAssignment<T>>,
): Set<string> {
  const roleAssignmentIdLabelSets: Set<string> = new Set()

  roleAssignments.forEach((roleAssignment) => {
    getRoleAssignmentIdLabels(roleAssignment).forEach((label) => {
      roleAssignmentIdLabelSets.add(label)
    })
  })

  return roleAssignmentIdLabelSets
}

export default diffProjectRoleAssignments
